// src/pages/BrandMasterPage.jsx
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { motion } from "framer-motion";
import { Plus, Edit2, Trash2, X, Search } from "lucide-react";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";

export default function BrandMasterPage() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  // modal / form states
  const [isOpen, setIsOpen] = useState(false);
  const [formName, setFormName] = useState("");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // table state
  const [queryText, setQueryText] = useState("");
  const [sortBy, setSortBy] = useState({ key: "name", dir: "asc" });
  const [page, setPage] = useState(1);
  const perPage = 12;

  // derived total pages (safe when brands changes)
  const filteredForCount = (brands || []).filter((b) =>
    b.name?.toLowerCase().includes(queryText.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredForCount.length / perPage));
  const pageSafe = Math.min(page, totalPages);

  // realtime listener
  useEffect(() => {
    const col = collection(db, "brands");
    const unsub = onSnapshot(
      col,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // ensure consistent sort by name by default
        arr.sort((a, b) => (a.name || "").toString().localeCompare(b.name || ""));
        setBrands(arr);
        setLoading(false);
      },
      (err) => {
        console.error("brands snapshot error:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // keyboard shortcuts: n => new modal, / => focus search, Escape => close modal
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "n") openCreate();
      if (e.key === "/") {
        const el = document.getElementById("brand-search");
        if (el) {
          e.preventDefault();
          el.focus();
        }
      }
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []); // run once

  // helpers
  const closeModal = () => {
    setIsOpen(false);
    setFormName("");
    setEditId(null);
    setError("");
  };

  const openCreate = () => {
    setFormName("");
    setEditId(null);
    setError("");
    setIsOpen(true);
  };

  const openEdit = (b) => {
    setFormName(b.name || "");
    setEditId(b.id);
    setError("");
    setIsOpen(true);
  };

  // duplicate check using nameLower field in Firestore (case-insensitive)
  const isDuplicateName = async (value, excludingId = null) => {
    const q = query(collection(db, "brands"), where("nameLower", "==", value.trim().toLowerCase()));
    const snap = await getDocs(q);
    if (snap.empty) return false;
    if (excludingId) {
      const others = snap.docs.filter((d) => d.id !== excludingId);
      return others.length > 0;
    }
    return true;
  };

  const handleSave = async () => {
    setError("");
    const trimmed = formName.trim();
    if (!trimmed) {
      setError("Brand name is required");
      return;
    }

    setSaving(true);
    try {
      const duplicate = await isDuplicateName(trimmed, editId);
      if (duplicate) {
        setError("A brand with this name already exists");
        setSaving(false);
        return;
      }

      if (editId) {
        await updateDoc(doc(db, "brands", editId), {
          name: trimmed,
          nameLower: trimmed.toLowerCase(),
          updatedAt: new Date(),
        });
      } else {
        await addDoc(collection(db, "brands"), {
          name: trimmed,
          nameLower: trimmed.toLowerCase(),
          createdAt: new Date(),
        });
      }

      closeModal();
    } catch (err) {
      console.error("save brand error:", err);
      setError("Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Confirm delete this brand?");
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "brands", id));
    } catch (err) {
      console.error("delete brand error:", err);
      alert("Delete failed");
    }
  };

  // derived list: filter, sort, paginate
  const filtered = (brands || []).filter((g) =>
    g.name?.toLowerCase().includes(queryText.toLowerCase())
  );

  const sorted = filtered.slice().sort((a, b) => {
    const A = (a[sortBy.key] || "").toString().toLowerCase();
    const B = (b[sortBy.key] || "").toString().toLowerCase();
    if (A < B) return sortBy.dir === "asc" ? -1 : 1;
    if (A > B) return sortBy.dir === "asc" ? 1 : -1;
    return 0;
  });

  const paged = sorted.slice((pageSafe - 1) * perPage, pageSafe * perPage);

  const toggleSort = (key) => {
    if (sortBy.key === key) setSortBy({ key, dir: sortBy.dir === "asc" ? "desc" : "asc" });
    else setSortBy({ key, dir: "asc" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Top MenuBar - full width */}
      <div className="w-full shadow z-50">
        <MenuBar />
      </div>

      {/* HeaderBar - below MenuBar */}
      <div className="w-full border-b shadow-sm z-40">
        <HeaderBar />
      </div>

      {/* Content area - scrollable */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-slate-800">Brand Master</h1>
              <p className="text-sm text-slate-500">Manage brands — realtime, searchable, and keyboard friendly.</p>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  id="brand-search"
                  value={queryText}
                  onChange={(e) => {
                    setQueryText(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search brands... ( / to focus )"
                  className="pl-9 pr-3 py-2 border rounded-md w-64 bg-white focus:ring-2 focus:ring-indigo-300"
                />
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              </div>

              <button
                onClick={openCreate}
                className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md shadow-md hover:bg-indigo-700"
                title="New (n)"
              >
                <Plus className="w-4 h-4" /> New
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Table header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="text-sm text-slate-600">{brands.length} total brands</div>
              <div className="flex items-center gap-4">
                <div className="text-sm text-slate-600">Sort:</div>
                <button
                  onClick={() => toggleSort("name")}
                  className={`px-2 py-1 rounded text-sm ${sortBy.key === "name" ? "bg-slate-100" : ""}`}
                >
                  Name {sortBy.key === "name" ? (sortBy.dir === "asc" ? "▲" : "▼") : ""}
                </button>
              </div>
            </div>

            {/* Table body */}
            <div className="p-4">
              {loading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-10 bg-gray-100 rounded" />
                  ))}
                </div>
              ) : (
                <div>
                  {paged.length === 0 ? (
                    <div className="py-12 text-center text-slate-500">
                      <p className="mb-3">No brands found.</p>
                      <button onClick={openCreate} className="inline-flex items-center gap-2 text-indigo-600">
                        <Plus className="w-4 h-4" /> Create first brand
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto text-sm">
                        <thead>
                          <tr className="text-left text-slate-600 border-b">
                            <th className="py-2 px-3">Name</th>
                            <th className="py-2 px-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paged.map((b) => (
                            <tr key={b.id} className="hover:bg-slate-50">
                              <td className="py-3 px-3 align-middle">
                                <div className="font-medium text-slate-800">{b.name}</div>
                                <div className="text-xs text-slate-400">
                                  {b.createdAt ? new Date(b.createdAt.seconds ? b.createdAt.seconds * 1000 : b.createdAt).toLocaleString() : ""}
                                </div>
                              </td>
                              <td className="py-3 px-3 text-right">
                                <div className="inline-flex gap-2">
                                  <button
                                    onClick={() => openEdit(b)}
                                    className="inline-flex items-center gap-2 px-3 py-1 rounded border hover:bg-yellow-50"
                                  >
                                    <Edit2 className="w-4 h-4" /> Edit
                                  </button>
                                  <button
                                    onClick={() => handleDelete(b.id)}
                                    className="inline-flex items-center gap-2 px-3 py-1 rounded border bg-rose-50 hover:bg-rose-100"
                                  >
                                    <Trash2 className="w-4 h-4 text-rose-600" /> Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      Showing {paged.length === 0 ? 0 : Math.min((pageSafe - 1) * perPage + 1, sorted.length)} - {Math.min(pageSafe * perPage, sorted.length)} of {sorted.length}
                    </div>
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => setPage(1)} disabled={pageSafe === 1} className="px-2 py-1 border rounded">First</button>
                      <button onClick={() => setPage(Math.max(1, pageSafe - 1))} disabled={pageSafe === 1} className="px-2 py-1 border rounded">Prev</button>
                      <div className="px-3 py-1 border rounded">Page {pageSafe} / {totalPages}</div>
                      <button onClick={() => setPage(Math.min(totalPages, pageSafe + 1))} disabled={pageSafe === totalPages} className="px-2 py-1 border rounded">Next</button>
                      <button onClick={() => setPage(totalPages)} disabled={pageSafe === totalPages} className="px-2 py-1 border rounded">Last</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal - add / edit */}
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={closeModal} />

          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-5 z-10">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold">{editId ? "Edit Brand" : "New Brand"}</h3>
                <p className="text-sm text-slate-500">Give a friendly name to the brand.</p>
              </div>
              <button onClick={closeModal} aria-label="close" className="p-2 rounded hover:bg-slate-100">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-4">
              <label className="block text-sm text-slate-700 mb-1">Name</label>
              <input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-300"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") closeModal();
                }}
                autoFocus
              />
              {error && <div className="text-rose-600 text-sm mt-2">{error}</div>}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={closeModal} className="px-3 py-2 rounded border">Cancel</button>
              <button onClick={handleSave} disabled={saving} className={`px-4 py-2 rounded ${saving ? "bg-gray-300" : "bg-indigo-600 text-white"}`}>
                {saving ? (editId ? "Updating..." : "Saving...") : editId ? "Update" : "Create"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
